import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Inter-team Tickets (views/tickets) and the notification switches that
 * govern where a ticket's notices go (views/settings, Notifications tab).
 *
 *   - `tickets`                  → the request; every write after filing is an
 *                                  ACTION (`patch(id, { action })`), never a
 *                                  field edit — the API refuses the latter
 *   - `ticket-events`            → one ticket's timeline, find-only, `?ticketId=`
 *   - `notification-preferences` → the caller's own opt-in switches per leg
 *                                  (`push` = this app's Web Push, `gantry` =
 *                                  their Gantry polling /ticket-feed); `create`
 *                                  REPLACES one leg's list
 *   - `push-subscriptions`       → the caller's own devices + the VAPID public key
 *
 * None of these is cached to storage: a ticket list is per-person and per-
 * moment, and a push endpoint is a credential to page someone.
 */
// enableOffline OFF is REQUIRED on all four: the library's offline store
// answers a connectivity failure with an OPTIMISTIC object, which the desk
// would take as "filed" and clear the draft for a ticket that was only queued
// in the browser — and a push subscription or a switch queued offline is a
// lie about what the server holds.
//
// THROWONERROR IS NOT SET, AND THAT DECIDES THE SHAPE OF EVERY FAILURE THESE
// FOUR STORES CAN HAVE. The library defaults it to false, so no call from any
// of them ever REJECTS: `find`, `create`, `patch` and `remove` each catch,
// record the reason on `errorOnFind` / `errorOnCreate` / `errorOnPatch` /
// `errorOnRemove`, and resolve `null`. That covers a transport error, a 403,
// and the wrapper's own bounded-call timeout alike.
//
// So in every consumer of these stores — TicketDeskStore, CommandCenterStore,
// NotificationSettingsStore — a `null` result IS the failure, and it is the
// only failure that occurs here; the reason has to be read off the matching
// `errorOn*` or the person is told "it did not work" and nothing else. Any
// try/catch around one of these calls is defence against a per-call
// `throwOnError` (which the library does honour) or a service double that
// throws in a test, not against anything this app produces at run time — cheap
// insurance, worth keeping, but never the path to design for.
//
// It is left off deliberately: these stores are read by several views at once
// and a throwing find would take down whichever one forgot to catch it. The
// price is that every consumer must treat null as an error, which is the rule
// stated above.
export const ticketsStores: ServiceStoreConfig[] = [
  {
    name: "tickets",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  {
    name: "ticket-events",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  {
    name: "notification-preferences",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  {
    name: "push-subscriptions",
    options: { idField: "id", enableStorage: false, enableOffline: false },
  },
  // The support e-mail HQ could not file on its own (views/tickets/SupportMail).
  // Never stored in the browser: an unmatched message may be anybody's mail.
  // `replaceOnFind`: each find is the whole list for one mode, so a message
  // placed elsewhere leaves the list on the next load instead of lingering.
  {
    name: "ticket-intake",
    options: { idField: "id", enableStorage: false, enableOffline: false, replaceOnFind: true },
  },
];
